// Components
import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  Header,
  Tabs,
} from '@amzn/awsui-components-react';
import { SignupList } from './signupsList';
import { ActivitySidebar } from './sideNav';
import { VolunteerActivitySummaryContainer } from './volunteerActivitySummary';
// Hooks
import { useLocalizedText } from '../../hooks/localizedText';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
// Utils
import {
  DISPLAY_ATTENDANCE_INFO,
  DISPLAY_DATE_INFO,
  DISPLAY_LOCATION_INFO,
} from '../shifts/shiftInfo';
import { useEffect, useRef, useState } from 'react';
import { PAGE_KEY } from '../../hooks/attendance/urlPage';
import { WaitlistsTab } from './waitlistsTab';
import { VolunteerEventsTab } from './volunteerEventsTab';
import { WaitlistInfoHelpPanel } from './waitlistInfoHelpPanel';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import {
  SHIFT_WAITLIST,
  SEARCH_AND_ACTIVITY_SHIFT_TABLE,
  NAVIGATION_REDESIGN,
  MY_ACTIVITY_SUMMARY,
} from '../../utils/constants';
import { AppOutletContext } from '../../App';

type TabType = 'upcoming' | 'past' | 'waitlists';

export function ActivityPage() {
  const { t } = useLocalizedText(['activity', 'translation']);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const features = useFeatures();
  // Two independent flags on this page:
  // * SHIFT_WAITLIST decides whether the Waitlists tab exists at all.
  // * SEARCH_AND_ACTIVITY_SHIFT_TABLE decides only what Upcoming/Past render — event-grouped shift
  //   table cards when on, the flat per-signup SignupList when off.
  const isWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  const isShiftTableEnabled = featureIsEnabled(
    SEARCH_AND_ACTIVITY_SHIFT_TABLE,
    features,
    searchParams
  );
  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const isSummaryEnabled = featureIsEnabled(MY_ACTIVITY_SUMMARY, features, searchParams);

  const tabParam = searchParams.get('tab') as TabType | null;
  const validTabs: TabType[] = isWaitlistEnabled
    ? ['upcoming', 'past', 'waitlists']
    : ['upcoming', 'past'];
  const activeTabId: TabType = tabParam && validTabs.includes(tabParam) ? tabParam : 'upcoming';

  const urlFeatures = searchParams.get('features');
  const priorityShiftId = searchParams.get('shiftId') ?? undefined;
  const volunteerActivityUrl = `/my-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;
  const now = useRef(new Date()).current;
  const [waitlistInfoOpen, setWaitlistInfoOpen] = useState(false);
  const outletContext = useOutletContext<AppOutletContext | null>();

  const toolsPanel = <WaitlistInfoHelpPanel />;

  // New nav: the root AppLayout owns the tools slot. Publish the waitlist help panel while the
  // waitlists tab is active so the tab's info button can open it, and clean it up otherwise.
  // In legacy the panel lives in this page's own AppLayout via the waitlistInfoOpen state below.
  // Without this, jumping straight into getContent() in new nav left the info button a no-op
  // (CR-291797692 rev 8).
  useEffect(() => {
    if (isNewNav && outletContext) {
      if (activeTabId === 'waitlists') {
        outletContext.setTools(toolsPanel);
      } else {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    }
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, activeTabId]);

  // Open the waitlist info help panel. In new nav the panel is owned by the root AppLayout
  // (opened via outletContext); in legacy it is local to this page's AppLayout.
  const openWaitlistInfo = () => {
    if (isNewNav && outletContext) {
      outletContext.setToolsOpen(true);
    } else {
      setWaitlistInfoOpen(true);
    }
  };

  const renderTabs = () => (
    <Tabs
      activeTabId={activeTabId}
      onChange={({ detail }) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', detail.activeTabId);
        // Reset pagination when switching tabs so each tab has its own page state.
        newParams.delete(PAGE_KEY);
        setSearchParams(newParams);
        // Close the Waitlists info panel when leaving the tab.
        setWaitlistInfoOpen(false);
      }}
      tabs={[
        {
          label: t('upcoming'),
          id: 'upcoming',
          content: isShiftTableEnabled ? (
            <VolunteerEventsTab tab="upcoming" />
          ) : (
            <SignupList
              startDate={now}
              sort="oldest-first"
              displayAttributes={[DISPLAY_DATE_INFO, DISPLAY_LOCATION_INFO]}
              priorityShiftId={priorityShiftId}
            />
          ),
        },
        {
          label: t('past'),
          id: 'past',
          content: isShiftTableEnabled ? (
            <VolunteerEventsTab tab="past" />
          ) : (
            <SignupList
              endDate={now}
              sort="newest-first"
              displayAttributes={[
                DISPLAY_DATE_INFO,
                DISPLAY_LOCATION_INFO,
                DISPLAY_ATTENDANCE_INFO,
              ]}
              includeAttendanceOnlyShifts={true}
            />
          ),
        },
        ...(isWaitlistEnabled
          ? [
              {
                label: t('waitlists'),
                id: 'waitlists',
                content: <WaitlistsTab onOpenInfo={openWaitlistInfo} />,
              },
            ]
          : []),
      ]}
    />
  );

  const getContent = () => {
    return (
      <div className="page-content">
        <ContentLayout
          header={
            <div className="page-content-header">
              <Header variant="h1">{t('pageTitle.volunteerActivity')}</Header>
            </div>
          }
        >
          {isSummaryEnabled && <VolunteerActivitySummaryContainer />}
          {renderTabs()}
        </ContentLayout>
      </div>
    );
  };

  // New navigation: return content only, shell handled by root AppLayout
  if (isNewNav) {
    return getContent();
  }

  // Legacy: keep existing AppLayout wrapper
  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={false}
      navigation={<ActivitySidebar baseUrl={volunteerActivityUrl} />}
      toolsHide={activeTabId !== 'waitlists'}
      toolsOpen={waitlistInfoOpen}
      onToolsChange={({ detail }) => setWaitlistInfoOpen(detail.open)}
      tools={<WaitlistInfoHelpPanel />}
      breadcrumbs={
        <div className="breadcrumbs">
          <BreadcrumbGroup
            items={[
              { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
              {
                text: t('breadcrumbs.volunteerActivity'),
                href: '#/',
              },
            ]}
            onFollow={(event) => {
              navigate(event.detail.href);
              event.preventDefault();
            }}
          />
        </div>
      }
      content={getContent()}
      contentType="cards"
    />
  );
}
